// ABOUTME: Pure point-tag placement helpers — sticky native-space anchor resolution (authored first,
// ABOUTME: region cascade second) and the screen-space de-clash pass that runs after clamping.

import {
  anchorForKey, resolveAnchor, regionForKey, listAnchors, nativeFrameSize,
} from './chapterTagAnchors';

// Plates are boxes, not points. The old 120px point-distance test let a region plate sit 217px
// left of an authored one and still be covered by it (2026-08-18 screenshot: "86 Hours" over
// "Micro-rotor" on the movement chapter) — anchors were far apart, the boxes were not. Two
// plates clash when their authored rectangles come within this clearance on both axes.
const PLATE_CLEARANCE_PX = 48;
const MAX_STACK_PROBES = 8;

const platesClash = (a, b) => (
  a.x < b.x + b.w + PLATE_CLEARANCE_PX && a.x + a.w + PLATE_CLEARANCE_PX > b.x
  && a.y < b.y + b.h + PLATE_CLEARANCE_PX && a.y + a.h + PLATE_CLEARANCE_PX > b.y
);

/**
 * Resolve a native-space anchor for every key, remembering previous answers so a tag NEVER moves
 * while it is on screen.
 *
 * WHY STICKY. The cascade below depends on which other tags are present, so re-running it from
 * scratch every render made a region-derived tag visibly TELEPORT to a higher free slot the moment
 * a neighbour faded out (part of the "jitter" reported 2026-08-25). The cache pins each key to the
 * first slot it was given for as long as it stays mounted; a key is only re-placed after it has
 * fully left the screen.
 *
 * TWO-PASS, WITH ALL AUTHORED PLATES RESERVED. Authored anchors are a designer's judgement and
 * always win (pass 1). The cascade (pass 2) then avoids EVERY authored plate of the chapter — not
 * just the currently-lit ones — because a region tag that borrows a dormant authored slot would
 * either collide with, or force a jump from, the authored tag when its key matches a moment later.
 * A reserved-but-never-lit plate costs one probe step; a collision costs the design.
 *
 * @param {object} params
 * @param {string[]} params.keys           content-bearing keys, in admission order. Cached slots
 *   are RETAINED for every key here (a fading tag must keep the anchor it is fading at)…
 * @param {Set<string>|null} [params.freshKeys]  …but NEW slots are only claimed for these keys.
 *   Callers pass their live (non-exiting) keys, so an entry already easing out can never grab a
 *   slot it will not meaningfully use. Omitted = every key may claim.
 * @param {Map<string, object>} params.cache  key -> anchor, owned by the caller (a ref), mutated here
 * @param {{current: string|null}} params.cacheSig  caller-owned signature ref; a chapter or
 *   breakpoint change invalidates every cached slot at once
 * @param {string|null} params.chapterId
 * @param {string} params.breakpoint
 * @param {object|null} params.tagRegions
 * @param {boolean} params.isMobile        mobile is a flow row — region anchors pass through as-is
 * @param {Array<{key: string, anchor: object}>} [params.coolingSlots]  SPENT slots — positions a
 *   tag already occupied earlier in this hold. A DIFFERENT key cascading into one fades in at the
 *   exact pixel an earlier plate vacated, which reads as one plate flickering/morphing rather than
 *   two tags (reported 2026-08-27, dial → markers). The cascade treats them as still occupied and
 *   only falls back to reusing them when every fresh slot is exhausted — reuse at the bottom of a
 *   long hold is better than silently dropping the tag. The SAME key returning may always reuse
 *   its own slot, because that genuinely is the same tag coming back.
 * @returns {(key: string) => object|null} lookup into the (now updated) cache
 */
export function resolveStickyAnchors({
  keys, freshKeys = null, cache, cacheSig, chapterId, breakpoint, tagRegions, isMobile,
  coolingSlots = [],
}) {
  const sig = `${chapterId}|${breakpoint}`;
  if (cacheSig.current !== sig) {
    cacheSig.current = sig;
    cache.clear();
  }
  // A key that has fully left the screen releases its slot for future arrivals.
  const wanted = new Set(keys);
  [...cache.keys()].forEach((k) => { if (!wanted.has(k)) cache.delete(k); });

  const mayClaim = (key) => !freshKeys || freshKeys.has(key);
  // The cascade may only place a box that actually lies inside the authored frame: an unbounded
  // downward probe (stackIndex * 357px) walks off the bottom on a deep stack, and an off-frame
  // slot clashes with nothing — it would be accepted, cached, and then silently dropped by the
  // crop-visibility filter with no diagnostic. Rejecting it here breaks the probe loop instead,
  // so the key lands in the caller's droppedKeys and WARNS.
  const frame = nativeFrameSize(breakpoint);
  const inFrame = (a) => a.y >= 0 && a.x >= 0
    && a.y + a.h <= frame.height && a.x + a.w <= frame.width;

  // Pass 1 — authored plates.
  keys.forEach((key) => {
    if (cache.has(key) || !mayClaim(key)) return;
    const authored = anchorForKey(chapterId, key, breakpoint);
    if (authored) cache.set(key, authored);
  });

  // Pass 2 — region cascade, against everything already settled plus every authored plate.
  const reserved = [
    ...cache.values(),
    ...listAnchors(breakpoint)
      .filter((a) => a.chapterId === chapterId)
      .map((a) => a.anchor),
  ];
  keys.forEach((key) => {
    if (cache.has(key) || !mayClaim(key)) return;
    if (isMobile) {
      // The mobile composition is a bottom-anchored flow row — coordinates carry no meaning and
      // every tag is laid out by MobileTagRow, so there is nothing to collide with.
      const anchor = resolveAnchor(chapterId, key, breakpoint, tagRegions, 0);
      if (anchor) cache.set(key, anchor);
      return;
    }
    if (!regionForKey(key, tagRegions)) return;
    const cooling = coolingSlots.filter((c) => c.key !== key).map((c) => c.anchor);
    const probe = (avoid) => {
      for (let i = 0; i < MAX_STACK_PROBES; i += 1) {
        const candidate = resolveAnchor(chapterId, key, breakpoint, tagRegions, i);
        if (!candidate || !inFrame(candidate)) return null;
        if (!avoid.some((t) => platesClash(candidate, t))) return candidate;
      }
      return null;
    };
    // Spent slots block first; when nothing fresh is left, reuse beats dropping the tag.
    const found = probe([...reserved, ...cooling]) ?? (cooling.length ? probe(reserved) : null);
    if (found) {
      cache.set(key, found);
      reserved.push(found);
    }
  });

  return (key) => cache.get(key) ?? null;
}

// De-clash geometry, all in percent-of-container units.
const DECLASH_GAP_PCT = 1.2;   // minimum daylight between two plates after adjustment
const MAX_DECLASH_STEPS = 8;   // 3-tag ceiling in practice; bounded for safety

const overlapsPct = (a, b) => (
  a.left < b.left + b.width + DECLASH_GAP_PCT && a.left + a.width + DECLASH_GAP_PCT > b.left
  && a.top < b.top + b.height + DECLASH_GAP_PCT && a.top + a.height + DECLASH_GAP_PCT > b.top
);

/**
 * Final screen-space overlap resolution, AFTER the safe-band clamps — sticky, like the anchors.
 *
 * The native-space cascade above guarantees clearance in the AUTHORED frame, but the safe-band
 * clamps run later in SCREEN space and can collapse two distinct anchors onto the same band — on a
 * wider-than-16:9 viewport the cover-crop pushes every lower-half anchor down until several hit the
 * footer ceiling together and pile up (the overlap reported 2026-08-25). This pass nudges the later
 * arrival vertically until it clears, preferring upward (into open frame) and falling back to
 * downward, giving up after a bounded number of steps rather than looping.
 *
 * Sticky for the same reason the anchors are: a de-clashed top recomputed from scratch would slide
 * back to its clamped position the moment the neighbour it dodged fades out — a visible jump on a
 * mounted tag. Each key keeps its first answer until it unmounts; only a viewport/chapter change
 * (a new `sig`) recomputes everything, because then the whole layout legitimately moved.
 *
 * @param {object} params
 * @param {Array<{key:string,top:number,left:number,width:number,height:number}>} params.rects
 *   percent units, in admission order; earlier rects are treated as settled
 * @param {Map<string, number>} params.cache  key -> settled top, owned by the caller (a ref)
 * @param {{current: string|null}} params.cacheSig  caller-owned signature ref
 * @param {string} params.sig  chapter|breakpoint|container dims — any change voids the cache
 * @param {{minTop:number, maxTop:number}} params.bounds  the same safe band the clamps used
 * @returns {number[]} adjusted `top` per rect, same order
 */
export function stickyScreenTops({ rects, cache, cacheSig, sig, bounds }) {
  if (cacheSig.current !== sig) {
    cacheSig.current = sig;
    cache.clear();
  }
  const wanted = new Set(rects.map((r) => r.key));
  [...cache.keys()].forEach((k) => { if (!wanted.has(k)) cache.delete(k); });

  const settled = [];
  return rects.map((rect) => {
    let current = cache.has(rect.key) ? { ...rect, top: cache.get(rect.key) } : { ...rect };
    if (!cache.has(rect.key)) {
      for (let step = 0; step < MAX_DECLASH_STEPS; step += 1) {
        const conflict = settled.find((s) => overlapsPct(current, s));
        if (!conflict) break;
        const above = conflict.top - current.height - DECLASH_GAP_PCT;
        const below = conflict.top + conflict.height + DECLASH_GAP_PCT;
        // Upward into open frame first; the footer ceiling is what caused the pile-up, so below
        // is only useful when the collision happened at the TOP band instead.
        const next = above >= bounds.minTop ? above : Math.min(below, bounds.maxTop);
        if (next === current.top) break; // both directions exhausted — accept the overlap
        current = { ...current, top: next };
      }
      cache.set(rect.key, current.top);
    }
    settled.push(current);
    return current.top;
  });
}
